import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { COLORS, AVATARS } from '../constants';
import { Card, Button, Input } from './UI';

// iOS Safari share icon (box-with-upward-arrow)
function IOSShareIcon() {
  return (
    <svg width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ display: "inline-block", verticalAlign: "text-bottom", margin: "0 2px" }}>
      <line x1="7.5" y1="12" x2="7.5" y2="1" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
      <polyline points="3.5,5 7.5,1 11.5,5" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 8H2a1 1 0 00-1 1v8a1 1 0 001 1h11a1 1 0 001-1V9a1 1 0 00-1-1h-1" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

export default function LoginScreen({ onParentLogin, onKidLogin }) {
  const [mode, setMode] = useState("choose");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [name, setName] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [kidCode, setKidCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeKids, setCodeKids] = useState([]);
  const [selectedKid, setSelectedKid] = useState(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Add-to-home-screen banner
  const deferredPrompt = useRef(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Already installed as PWA — don't show
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Desktop — don't show
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
    if (!isMobile) return;

    // Dismissed recently (within 7 days) — don't show
    const dismissed = localStorage.getItem('aths_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS Safari never fires beforeinstallprompt — show manual instructions immediately
      setShowBanner(true);
    } else {
      const handler = (e) => {
        e.preventDefault();
        deferredPrompt.current = e;
        setShowBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleATHS = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setShowBanner(false);
  };

  const dismissBanner = () => {
    localStorage.setItem('aths_dismissed', String(Date.now()));
    setShowBanner(false);
  };

  const handleParentAuth = async () => {
    if (!email || !pass) return;
    setLoading(true);
    setError("");
    const trimmedEmail = email.trim();
    const trimmedPass = pass.trim();
    try {
      if (isSignup) {
        const familyCode = "TT" + Math.random().toString(36).substring(2, 7).toUpperCase();
        const { data, error: authError } = await supabase.auth.signUp({ email: trimmedEmail, password: trimmedPass });
        if (authError) throw authError;
        const { error: dbError } = await supabase.from('parents').insert({ id: data.user.id, email: trimmedEmail, name: name || trimmedEmail.split('@')[0], family_code: familyCode });
        if (dbError) throw dbError;
        const { data: parent } = await supabase.from('parents').select('*').eq('id', data.user.id).single();
        // Send welcome email — non-blocking, failure must never break signup
        fetch('https://jgxbgtyjlgnsnkzmtxeq.supabase.co/functions/v1/welcome-parent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parent_email: trimmedEmail, parent_name: name || trimmedEmail.split('@')[0] }),
        }).catch(err => console.error('Welcome email failed:', err));
        onParentLogin(parent);
      } else {
        const { data, error: authError } = await supabase.auth.signInWithPassword({ email: trimmedEmail, password: trimmedPass });
        if (authError) throw authError;
        let { data: parent } = await supabase.from('parents').select('*').eq('id', data.user.id).single();
        if (!parent) {
          const familyCode = "TT" + Math.random().toString(36).substring(2, 7).toUpperCase();
          await supabase.from('parents').insert({ id: data.user.id, email, name: name || email.split('@')[0], family_code: familyCode });
          const { data: newParent } = await supabase.from('parents').select('*').eq('id', data.user.id).single();
          parent = newParent;
        }
        onParentLogin(parent);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const verifyCode = async () => {
    setLoading(true);
    setCodeError("");
    const { data } = await supabase.from('parents').select('id').eq('family_code', kidCode.toUpperCase()).single();
    if (!data) { setCodeError("That code doesn't match. Ask a parent!"); setLoading(false); return; }
    const { data: kids } = await supabase.from('children').select('*').eq('parent_id', data.id);
    setCodeKids(kids || []);
    setLoading(false);
    if (kids && kids.length > 0) setMode("kidPick");
    else setCodeError("No kids added yet. Ask a parent to add you!");
  };

  const verifyPin = (enteredPin) => {
    if (enteredPin === selectedKid.pin) {
      onKidLogin(selectedKid);
    } else {
      setPinError("Wrong PIN. Try again!");
      setPin("");
    }
  };

  const PinPad = ({ value, onChange, onSubmit, error: pinErr }) => (
    <div style={{ textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 20 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ width: 48, height: 48, borderRadius: 12, border: `2px solid ${value.length > i ? COLORS.primary : COLORS.border}`, background: value.length > i ? COLORS.accentLighter : COLORS.surfaceGreen, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800, color: COLORS.primary }}>
            {value.length > i ? "●" : ""}
          </div>
        ))}
      </div>
      {pinErr && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#E63946", marginBottom: 12, fontWeight: 700 }}>{pinErr}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, maxWidth: 240, margin: "0 auto" }}>
        {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k, i) => (
          <button key={i} onClick={() => {
            if (k === "⌫") { onChange(value.slice(0,-1)); setPinError(""); }
            else if (k !== "" && value.length < 4) { const next = value + k; onChange(next); if (next.length === 4) setTimeout(() => onSubmit(next), 200); }
          }} style={{ padding: "16px 0", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: k === "" ? "transparent" : COLORS.surfaceGreen, fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 20, color: COLORS.text, cursor: k === "" ? "default" : "pointer", visibility: k === "" ? "hidden" : "visible" }}>{k}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${COLORS.accentLighter} 0%, ${COLORS.white} 60%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 42, color: COLORS.primary, letterSpacing: 1 }}>ThriveTrack</div>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 15, color: COLORS.textMuted, marginTop: 4 }}>Helping families grow together 🌱</div>
        </div>

        {mode === "choose" && (
          <Card style={{ padding: 28 }}>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 16, fontWeight: 800, color: COLORS.text, textAlign: "center", marginBottom: 20 }}>Who's logging in?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button onClick={() => setMode("parent")} style={{ padding: "20px", borderRadius: 16, border: `2px solid ${COLORS.border}`, background: COLORS.surfaceGreen, cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ fontSize: 40 }}>👨‍👩‍👧</div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 16, color: COLORS.text }}>I'm a Parent</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>Manage missions, rewards & insights</div>
                </div>
              </button>
              <button onClick={() => setMode("kidCode")} style={{ padding: "20px", borderRadius: 16, border: `2px solid #FFD93D`, background: "#FFFBEB", cursor: "pointer", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ fontSize: 40 }}>🧒</div>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 16, color: COLORS.text }}>I'm a Kid</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>Check missions & earn rewards</div>
                </div>
              </button>
            </div>
          </Card>
        )}

        {mode === "parent" && (
          <Card style={{ padding: 28 }}>
            <button onClick={() => setMode("choose")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.textMuted, marginBottom: 16, fontSize: 13 }}>← Back</button>
            <div style={{ display: "flex", gap: 8, marginBottom: 20, background: COLORS.surfaceGreen, borderRadius: 12, padding: 4 }}>
              {["Sign In", "Sign Up"].map((m, i) => (
                <button key={m} onClick={() => setIsSignup(i === 1)} style={{ flex: 1, padding: 10, borderRadius: 10, border: "none", cursor: "pointer", background: isSignup === (i === 1) ? COLORS.primary : "transparent", color: isSignup === (i === 1) ? "#fff" : COLORS.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, transition: "all 0.2s" }}>{m}</button>
              ))}
            </div>
            {isSignup && <Input label="Your Name" value={name} onChange={setName} placeholder="Parent name" />}
            <Input label="Email" value={email} onChange={setEmail} placeholder="you@email.com" type="email" />
            <Input label="Password" value={pass} onChange={setPass} placeholder="••••••••" type="password" />
            {error && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#E63946", marginBottom: 12, fontWeight: 600 }}>{error}</div>}
            <Button onClick={handleParentAuth} size="lg" style={{ width: "100%" }} disabled={loading}>
              {loading ? "Please wait..." : isSignup ? "Create Account →" : "Sign In →"}
            </Button>
          </Card>
        )}

        {mode === "kidCode" && (
          <Card style={{ padding: 28, textAlign: "center" }}>
            <button onClick={() => setMode("choose")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.textMuted, marginBottom: 16, fontSize: 13, display: "block" }}>← Back</button>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔑</div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: COLORS.text, marginBottom: 8 }}>Enter Family Code</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted, marginBottom: 20 }}>Ask a parent for your family code!</div>
            <input value={kidCode} onChange={e => setKidCode(e.target.value.toUpperCase())} placeholder="e.g. TT4X2B"
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: `2px solid ${codeError ? "#E63946" : COLORS.border}`, fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: COLORS.primary, background: COLORS.surfaceGreen, outline: "none", textAlign: "center", letterSpacing: 4, boxSizing: "border-box" }} />
            {codeError && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#E63946", marginTop: 8, fontWeight: 700 }}>{codeError}</div>}
            <Button onClick={verifyCode} variant="kid" size="lg" style={{ width: "100%", marginTop: 16 }} disabled={loading}>
              {loading ? "Checking..." : "Let's Go! 🚀"}
            </Button>
          </Card>
        )}

        {mode === "kidPick" && (
          <Card style={{ padding: 28 }}>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: COLORS.text, textAlign: "center", marginBottom: 20 }}>Who are you? 👋</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {codeKids.map(child => (
                <button key={child.id} onClick={() => { setSelectedKid(child); setMode("kidPin"); }} style={{ padding: "16px 20px", borderRadius: 14, border: `2px solid ${COLORS.border}`, background: COLORS.surfaceGreen, cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ fontSize: 36 }}>{child.avatar || "🧒"}</div>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 18, color: COLORS.text }}>{child.name}</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>Age {child.age} · {child.points} coins ⭐</div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        )}

        {mode === "kidPin" && selectedKid && (
          <Card style={{ padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 56, marginBottom: 8 }}>{selectedKid.avatar || "🧒"}</div>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: COLORS.text, marginBottom: 4 }}>Hey {selectedKid.name}! 👋</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted, marginBottom: 24 }}>Enter your 4-digit PIN</div>
            <PinPad value={pin} onChange={v => { setPin(v); setPinError(""); }} onSubmit={verifyPin} error={pinError} />
            <button onClick={() => { setSelectedKid(null); setMode("kidPick"); setPin(""); setPinError(""); }} style={{ marginTop: 20, background: "none", border: "none", cursor: "pointer", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.textMuted, fontSize: 13 }}>← Choose different kid</button>
          </Card>
        )}
      </div>

      {/* Add to Home Screen banner */}
      {showBanner && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: COLORS.primary,
          padding: "14px 16px 18px",
          zIndex: 2000,
          boxShadow: "0 -4px 24px rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isIOS ? (
              <>
                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: "#fff", lineHeight: 1.3, marginBottom: 4 }}>
                  📲 Add ThriveTrack to your home screen!
                </div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>
                  Tap <IOSShareIcon /> in Safari, then <strong style={{ color: "#fff" }}>"Add to Home Screen"</strong>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: "#fff", lineHeight: 1.3, marginBottom: 4 }}>
                  📲 Add ThriveTrack to your home screen
                </div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
                  For the best experience — works offline too!
                </div>
              </>
            )}
          </div>

          {/* Add button — only for non-iOS (iOS needs manual steps) */}
          {!isIOS && (
            <button
              onClick={handleATHS}
              style={{
                background: "#fff",
                color: COLORS.primary,
                border: "none",
                borderRadius: 10,
                padding: "9px 18px",
                fontFamily: "Nunito, sans-serif",
                fontWeight: 800,
                fontSize: 13,
                cursor: "pointer",
                flexShrink: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              }}
            >
              Add
            </button>
          )}

          {/* Dismiss */}
          <button
            onClick={dismissBanner}
            aria-label="Dismiss"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
